
import Vue from "vue"
import * as _ from "lodash"
import * as am4core from "@amcharts/amcharts4/core"
import {Color, Percent, Sprite} from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import {ColumnSeriesDataItem, PieChart, PieSeriesDataItem} from "@amcharts/amcharts4/charts"
import * as am4maps from "@amcharts/amcharts4/maps"
import {MapImage, MapImageSeries, MapPolygon} from "@amcharts/amcharts4/maps"
import Component from "vue-class-component"
import {cubejsApi, QueryData} from "@/cube/cubejsApi"
import {chartTheme} from "@/ui/chartTheme"
import {QChip, QToggle} from "quasar"
import type {Dimension, Measure} from "@/cube"
import {MemberCube, MemberSnapshotCube, Metric} from "@/cube"
import {Inject, Prop} from "vue-property-decorator"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import {dropShadowFilter} from "@/ui/chart/dropShadowFilter"
import {DateRange, DateRangeSelector} from "@/DateRangeSelector"
import {PlanService} from "@/superannuation/PlanService"
import {calculateChangeRatePercent} from "@/ui/chart/calculateChangeRatePercent"
import {createChangeRateLabel} from "@/ui/chart/updateChangeRate"
import {toCubeFormat} from "@/cube/toCubeFormat"
import {fillDimensionCategories} from "@/utils/fillDimensionCategories"
import DimensionSelect from "@/ui/DimensionSelect.vue"
import australiaLow from "@amcharts/amcharts4-geodata/australiaLow"
import {toKey} from "@/cube/toKey"
import {keyedDataUpdate} from "@/cube/keyedDataUpdate"
import {dimensionSettings, measureSettings} from "@/cube/settings"
import {formatValue} from "@/utils/formatter"
import {DateRangeService} from "@/DateRangeService"

am4core.useTheme(chartTheme)

@Component({components: {QChip, QToggle, DimensionSelect}})
export default class ByChart extends Vue {
  @Prop({required: true})
  measure: Measure

  // TODO rcosta remove null here might be bad not sure
  @Prop({required: true})
  dimensions: Array<Dimension>

  @Prop({required: true})
  readonly secondaryDimension: Dimension

  @Prop()
  dateRangeSelector?: DateRangeSelector

  @Inject()
  planService: PlanService

  @Inject()
  dateRangeService: DateRangeService

  private selectedDimension: Dimension = <any>null

  private chart?: am4charts.XYChart
  private mapChart: am4maps.MapChart | null
  private allData: Array<any> = []
  private dataAnnotation: any = null
  private mapImageSeries: MapImageSeries
  private isLoading = false
  private isPercentualViewMode = false

  private resultSet: any

  get isState() {
    return this.selectedDimension === MemberCube.addressState
  }

  get baseQuery() {
    // TODO rcosta revisit this.dimensions declaration/usage
    const dimensions = [...this.dimensions, this.selectedDimension]

    return <const>{
      timeDimensions: [
        {
          dimension: MemberSnapshotCube._at,
          granularity: <const>"month",
        },
      ],
      measures: [this.measure],
      dimensions,
    }
  }

  async created() {
    this.selectedDimension = this.secondaryDimension

    this.$watch(
      () => [this.planService.current, this.dateRangeSelector].join(),
      async () => {
        if (this.planService.current != null && this.dateRangeSelector?.selected != null) {
          this.watchQuery()
          this.watchDateRangeSelector()
        }
      },

      {immediate: true}
    )

    this.$watch(
      () => this.dateRangeSelector?.selected != null && !_.isEmpty(this.allData),
      this.updateChart
    )

    this.$watch(
      () => this.planService.current?.id,
      async () => {
        await this.dateRangeService.setPlan(this.planService.current!.id)
        await this.prepareChartData()
        this.updateChart()
      }
    )

    this.$watch(
      () => this.planService.engagedOnly,
      async () => {
        await this.prepareChartData()
        this.updateChart()
      }
    )
  }

  beforeDestroy() {
    if (this.chart != null) {
      this.chart.dispose()
    }

    if (this.mapChart != null) {
      this.mapChart.dispose()
    }
  }

  updateChartWith(dateRange: DateRange) {
    // TODO keep start and only eval end
    const dataWithStartDate = findDataWithStartDate(this.allData, dateRange.start)

    let currentEndData
    if (this.isState) {
      currentEndData = findDataWithEndDate(this.allData, dateRange.end)

      this.updateChartChangeRate(dataWithStartDate, currentEndData)

      this.mapImageSeries.mapImages.values.forEach(mapImage => {
        mapImage.children.values.forEach((pieChart: PieChart) => {
          pieChart.series.values[0].invalidateLabels()
        })
      })
    } else {
      currentEndData = _.flatMap(this.chart!.series.values, series => series.data)

      this.updateChartChangeRate(dataWithStartDate, currentEndData)

      console.log("Updated change rate values", this.chart!.series.values[0].data)
    }
  }

  async onPercentualViewModeChange() {
    // TODO switching mode doesn't work
    // this.applyViewMode(this.chart)
    this.updateChart()
  }

  createChart(selectedData: Array<unknown>) {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.XYChart)
    chart.responsive.enabled = true
    chart.tapToActivate = true
    chart.chartContainer.height = 450

    console.log("selectedData", selectedData)

    const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
    categoryAxis.data = selectedData
    categoryAxis.dataFields.category = "category"
    categoryAxis.renderer.inversed = true
    categoryAxis.renderer.grid.template.location = 0
    categoryAxis.fontWeight = "100"

    // Value axis is based on the measure

    const measureName = this.baseQuery.measures[0]

    const source = measureSettings[measureName]?.valueAxis

    const valueAxis = chart.xAxes.push(new am4charts.ValueAxis())

    valueAxis.renderer.opposite = true
    console.log(`Metric settings for ${measureName}`, source)
    if (source != null) {
      Object.assign(valueAxis, source)
      console.log(`settings applied for ${measureName}`)
    }

    const dataByGroup = this.getDataByGroup(selectedData)
    console.log("dataByGroup", dataByGroup)
    this.populateSeries(chart, dataByGroup, measureName, this.selectedDimension)

    this.applyViewMode(chart)

    // add chart cursor
    chart.cursor = new am4charts.XYCursor()

    chart.legend = new am4charts.Legend()
    chart.legend.paddingTop = am4core.percent(2.5)
    chart.legend.paddingBottom = 80
    chart.legend.labels.template.fontWeight = "100"

    chart.xAxes.values.forEach(axis => (axis.renderer.grid.template.strokeDasharray = "3,3"))
    chart.yAxes.values.forEach(axis => (axis.renderer.grid.template.strokeDasharray = "3,3"))

    if (this.chart != null) {
      // FIXME doesn't render the new chart
      // this.chart.dispose()
    }

    this.chart = chart
    this.isLoading = false
  }

  createMapChart(selectedData: QueryData) {
    const chart = am4core.create((<any>this.$refs).chart, am4maps.MapChart)
    chart.mouseWheelBehavior = "none"
    chart.tapToActivate = true

    // On mobile can still zoom
    chart.maxZoomLevel = 4

    chart.geodata = australiaLow
    chart.seriesContainer.draggable = false
    chart.padding(0, 30, 150, 10)

    const polygonSeries = new am4maps.MapPolygonSeries()
    polygonSeries.useGeodata = true
    polygonSeries.calculateVisualCenter = true

    const shadowFilter = dropShadowFilter()
    shadowFilter.height = 120
    polygonSeries.filters.push(shadowFilter)

    const polygonSeriesTemplate = polygonSeries.mapPolygons.template
    // FIXME use theme
    polygonSeriesTemplate.fill = am4core.color("#152773")
    polygonSeriesTemplate.fillOpacity = 0.2

    chart.series.push(polygonSeries)

    this.mapImageSeries = chart.series.push(new am4maps.MapImageSeries())
    this.mapImageSeries.zIndex = 2
    this.mapImageSeries.filters.push(shadowFilter)

    const mapImageTemplate = this.mapImageSeries.mapImages.template
    mapImageTemplate.propertyFields.latitude = "latitude"
    mapImageTemplate.propertyFields.longitude = "longitude"

    const pieChartTemplate = this.createPieChart()
    pieChartTemplate.parent = mapImageTemplate

    const dataByState = groupDataByState(selectedData)

    this.mapImageSeries.data = createMapImageSeriesData(dataByState)

    console.log("mapImageSeries", this.mapImageSeries.data)

    const colorSet = new am4core.ColorSet()
    const legendData: Array<unknown> = []
    _.map(_.uniqBy(selectedData, "category"), element => {
      const color =
        dimensionSettings(this.dimensions![0], (<any>element).category).color ?? colorSet.next()

      legendData.push({
        name: (<any>element).category,
        fill: color,
        stroke: color,
      })
    })

    if (this.dimensions.length > 0) {
      const legend = new am4charts.Legend()
      const marker = legend.markers.template.children.getIndex(0)!
      marker.fillOpacity = 0.35
      marker.strokeWidth = 1
      marker.strokeOpacity = 1
      marker.propertyFields.stroke = "stroke"
      legend.fontWeight = "lighter"
      legend.data = legendData
      legend.parent = chart.chartContainer
      legend.valign = "bottom"
      legend.contentAlign = "center"
      legend.paddingBottom = am4core.percent(0)
      legend.paddingTop = am4core.percent(15.3)
      legend.width = am4core.percent(100)
      legend.height = am4core.percent(22)
      legend.interactionsEnabled = false
    }

    // Disable default handling/zoom behaviour
    chart.seriesContainer.events.disableType("doublehit")
    chart.chartContainer.background.events.disableType("doublehit")

    const zoomHandler = (event: {type: "doublehit"; target: MapImage | MapPolygon}) => {
      const duration = 500
      if (chart.zoomLevel > 1.5) {
        chart.goHome(duration)
      } else {
        chart.zoomToMapObject(event.target, 4, true, duration)
      }
    }
    polygonSeriesTemplate.events.on("doublehit", zoomHandler)
    mapImageTemplate.events.on("doublehit", zoomHandler)

    this.mapChart = chart
    this.isLoading = false
  }

  createPieChart() {
    const pieChartTemplate = new am4charts.PieChart()
    pieChartTemplate.adapter.add("data", (data: any, target: am4core.Sprite) => {
      if (target.dataItem) {
        return (<any>target.dataItem.dataContext).pieData
      } else {
        return []
      }
    })

    pieChartTemplate.propertyFields.radius = "radius"
    pieChartTemplate.horizontalCenter = "middle"
    pieChartTemplate.verticalCenter = "middle"
    const pieSeriesTemplate = pieChartTemplate.series.push(new am4charts.PieSeries())
    pieSeriesTemplate.dataFields.category = "category"
    pieSeriesTemplate.dataFields.value = this.measure
    pieSeriesTemplate.ticks.template.disabled = true
    pieSeriesTemplate.alignLabels = false

    const fillModifier = new am4core.LinearGradientModifier()
    fillModifier.brightnesses = [0.35]
    fillModifier.lightnesses = [0.5]

    pieSeriesTemplate.slices.template.fillModifier = fillModifier
    pieSeriesTemplate.slices.template.adapter.add("fill", (fill, target) => {
      const {color} = dimensionSettings(
        this.dimensions![0],
        (<{category: string}>target.dataItem?.dataContext).category
      )
      return color === undefined ? fill : am4core.color(color)
    })

    if (this.baseQuery.dimensions.length === 1) {
      const valueLabelContainer = pieSeriesTemplate.bulletsContainer.createChild(am4core.Container)
      const valueLabel = valueLabelContainer.createChild(am4charts.AxisLabelCircular)
      valueLabel.verticalCenter = "middle"
      valueLabel.horizontalCenter = "middle"
      valueLabel.fill = am4core.color("white")
      valueLabel.adapter.add("text", (value, target) => {
        const dataItemValue = formatValue(
          this.measure,
          target.dataItem?.dataContext.pieData[0][this.measure]
        )

        target.setVisibility(true)

        return dataItemValue
      })

      pieSeriesTemplate.tooltip!.disabled = true
    }
    pieSeriesTemplate.slices.template.adapter.add("tooltipText", (value, target) => {
      const dataItem = <PieSeriesDataItem | undefined>target.dataItem
      if (dataItem === undefined) return undefined
      const sliceValue = formatValue(this.measure, dataItem.value)
      return `{category}: {value.percent.formatNumber('#.#')}% (${sliceValue})`
    })
    pieSeriesTemplate.labels.template.radius = am4core.percent(0.5)
    pieSeriesTemplate.labels.template.adapter.add(
      "html",
      (value, target: am4charts.AxisLabelCircular) =>
        target.dataItem === undefined || target.dataItem.value === 0
          ? " "
          : createChangeRateLabel(
              this.dimensions[0],
              removeDimensionSortPrefix(target.dataItem?.dataContext[this.baseQuery.dimensions[0]]),
              {
                value: target.dataItem?.dataContext.changeRate,
                unit: "%",
              }
            )
    )

    return pieChartTemplate
  }

  private watchDateRangeSelector() {
    const updateValuesDebounced = _.debounce(
      (dateRange: DateRange) => this.updateChartWith(dateRange),
      350
    )
    // TODO merge watches on dateRangeSelector and react according to the dates
    // that have changed
    this.$watch(
      () =>
        this.dateRangeSelector == null || this.dateRangeSelector.selected == null
          ? null
          : this.dateRangeSelector.selected.start,
      (n, o) => {
        console.log("this.dateRangeSelector.selected.start changed", n, o)
        if (
          n != null &&
          o != null &&
          n.getTime() !== o.getTime() &&
          this.dateRangeSelector?.selected != null
        ) {
          updateValuesDebounced(this.dateRangeSelector.selected)
        }
      }
    )

    const updateSeriesDebounced = _.debounce(
      (dateRange: DateRange) => this.updateSeries(dateRange, this.isState),
      350
    )
    this.$watch(
      () =>
        this.dateRangeSelector == null || this.dateRangeSelector.selected == null
          ? null
          : this.dateRangeSelector.selected.end,
      (n, o) => {
        console.log("this.dateRangeSelector.selected.end changed", n, o)
        if (
          n != null &&
          o != null &&
          n.getTime() !== o.getTime() &&
          this.dateRangeSelector?.selected != null
        ) {
          updateSeriesDebounced(this.dateRangeSelector.selected)
        }
      }
    )
  }

  private watchQuery() {
    this.$watch(
      () => this.baseQuery,
      async () => {
        if (this.planService.current != null) {
          await this.prepareChartData()

          this.updateChart()
        }
      },
      {immediate: true}
    )
  }

  private async prepareChartData() {
    console.log("Query changed")
    this.isLoading = true
    this.resultSet = await this.runQuery()

    this.allData = fillDimensionCategories(
      <Array<unknown>>this.resultSet.loadResponse.results[0].data,
      MemberSnapshotCube._at,
      this.measure
    )

    this.dataAnnotation = this.resultSet.loadResponse.results[0].annotation

    console.log("all data (filled dimensions)", this.allData)

    console.log("Meta", this.dataAnnotation.measures, this.dimensions)
    // Category axis is based on the main dimensions, when empty it
    // still aggregates it under a blank category

    const sourceDimension = this.dimensions
    this.allData.forEach(
      (dataItem: {category: string}) => (dataItem.category = toCategory(dataItem, sourceDimension))
    )
  }

  private updateSeries(dateRange: DateRange, isAddressStateDimension: boolean) {
    const dataWithStartDate = findDataWithStartDate(this.allData, dateRange.start)

    const dataWithEndDate = findDataWithEndDate(this.allData, dateRange.end)

    if (isAddressStateDimension) {
      this.updateChartChangeRate(dataWithStartDate, dataWithEndDate)

      const dataByState = groupDataByState(dataWithEndDate)
      this.mapImageSeries.mapImages.values.forEach(mapImage => {
        mapImage.children.values.forEach((pieChart: PieChart) => {
          const stateInitials = (<{id: string}>pieChart.series.values[0].dataItem!.dataContext).id
          const category = <Dimension>"category"
          const changeRate = "changeRate"
          pieChart.data.forEach(seriesData => {
            const updatedDataForState = _.find(
              dataByState[stateInitials],
              // FIXME simplify this / duplicate code?
              // eslint-disable-next-line max-nested-callbacks
              stateDataItem => stateDataItem[category] === seriesData[category]
            )
            seriesData[this.measure] = updatedDataForState?.[this.measure]
            // FIXME need to remove changeRate from being added to end data
            seriesData[changeRate] = (<any>updatedDataForState)?.[changeRate]
          })
          pieChart.invalidateRawData()
        })
      })
    } else {
      this.chart?.series.values.forEach(series => {
        keyedDataUpdate(
          this.measure,
          (row: any) => toKey(row, this.baseQuery.dimensions),
          dataWithEndDate,
          series.data
        )
      })

      this.chart?.invalidateRawData()

      this.applyViewMode(this.chart!)
      this.updateChartChangeRate(
        dataWithStartDate,
        _.flatMap(this.chart!.series.values, series => series.data)
      )
    }
  }

  private updateChart() {
    if (this.dateRangeSelector?.selected == null) {
      return
    }

    const dataWithEndDate = findDataWithEndDate(this.allData, this.dateRangeSelector.selected.end)
    console.log("dataWithEndDate", dataWithEndDate)

    const dataWithStartDate = findDataWithStartDate(
      this.allData,
      this.dateRangeSelector.selected.start
    )

    console.log("dataWithStartDate", dataWithStartDate)

    const copy = JSON.parse(JSON.stringify(dataWithEndDate))

    if (this.isState) {
      this.createMapChart(copy)
      this.updateChartChangeRate(dataWithStartDate, copy)
    } else {
      if (this.chart != null) {
        this.chart.dispose()
      }

      this.createChart(copy)
      this.updateChartChangeRate(dataWithStartDate, copy)
    }
  }

  private updateChartChangeRate(dataWithStartDate: QueryData, dataWithEndDate: QueryData) {
    updateChangeRateValues(
      dataWithStartDate,
      dataWithEndDate,
      this.baseQuery.measures[0],
      this.baseQuery.dimensions,
      this.isState
    )

    if (this.isState) {
      const dataByState = groupDataByState(dataWithEndDate)
      this.mapImageSeries.mapImages.values.forEach(mapImage => {
        mapImage.children.values.forEach((pieChart: PieChart) => {
          const stateInitials = (<{id: string}>pieChart.series.values[0].dataItem!.dataContext).id
          const category = <Dimension>"category"
          const changeRate = "changeRate"
          pieChart.data.forEach(seriesData => {
            const updatedDataForState = _.find(
              dataByState[stateInitials],
              // FIXME simplify this / duplicate code?
              // eslint-disable-next-line max-nested-callbacks
              stateDataItem => stateDataItem[category] === seriesData[category]
            )
            // FIXME need to remove changeRate from being added to end data
            seriesData[changeRate] = (<any>updatedDataForState)?.[changeRate]
          })
        })
      })
    } else {
      this.chart!.series.values.forEach(series => {
        const changeRateBullet = <Sprite | null>series.bullets.values[1]

        if (changeRateBullet != null) {
          series.bullets.removeValue(changeRateBullet)
          // Just removing from bullets is not enough as it leaves an disposed
          // copy which creates issues with the cursor tooltip
          series.dataItems.values.forEach(dataItem =>
            _.remove(dataItem.sprites, sprite => sprite.isDisposed())
          )
        }

        if (!this.isPercentualViewMode) {
          series.bullets.push(newChangeRateBullet(this.baseQuery.dimensions[0]))
        }
      })
    }
  }

  private async runQuery() {
    const order = _.chain(this.baseQuery.dimensions)
      .map(dimension => [dimension, "asc"])
      // TODO use asc?
      .push([MemberSnapshotCube._at, "desc"])
      .fromPairs()
      .value()

    const filters = [
      {
        dimension: "Member.planId",
        operator: "equals",
        values: [this.planService.current!.id],
      },
    ].concat(
      this.planService.engagedOnly
        ? [
            {
              dimension: "Member.engaged",
              operator: "equals",
              values: ["1"],
            },
          ]
        : []
    )

    const query = {...this.baseQuery, filters, order}
    console.log("Running query:", query)
    return cubejsApi.load(query)
  }

  private applyViewMode(chart: am4charts.XYChart) {
    console.log("Applying view mode [isPercentualViewMode]:", this.isPercentualViewMode)
    const valueAxis = <am4charts.ValueAxis>chart.xAxes.values[0]

    const measureShortTitle = <string>this.dataAnnotation.measures[this.measure].shortTitle

    valueAxis.title.text = this.isPercentualViewMode ? `${measureShortTitle} %` : measureShortTitle

    valueAxis.calculateTotals = true

    if (this.isPercentualViewMode) {
      valueAxis.max = 100
      valueAxis.strictMinMax = true

      for (const chartSeries of chart.series) {
        console.log("Updating view mode for", chartSeries)
        chartSeries.stacked = true
        chartSeries.dataFields.valueXShow = "totalPercent"
        const bullet = <am4charts.LabelBullet>chartSeries.bullets.values[0]
        bullet.label.text = "{valueX.totalPercent.formatNumber('###.')}%"
      }
    } else {
      valueAxis.renderer.labels.template.adapter.add("text", value =>
        value === undefined ? value : formatValue(this.measure, value, true)
      )

      valueAxis.adapter.add("getTooltipText", text => formatValue(this.measure, text))

      const axisSettings = measureSettings[this.measure]?.valueAxis
      console.log(`SETTINGS for ${this.measure}`, axisSettings)

      if (axisSettings != null) {
        Object.assign(valueAxis, axisSettings)
      }

      // Ensure that it always leaves some room for the change rate
      valueAxis.extraMax = 0.1

      for (const chartSeries of chart.series) {
        chartSeries.stacked = false
        chartSeries.dataFields.valueXShow = undefined
        const bullet = <am4charts.LabelBullet>chartSeries.bullets.values[0]
        bullet.label.adapter.add("text", (value, target) =>
          formatValue(this.measure, (<ColumnSeriesDataItem>target.dataItem).valueX)
        )
      }
    }
  }

  private getDataByGroup(data: Array<unknown>) {
    return _.groupBy(data, this.selectedDimension)
  }

  private populateSeries(
    chart: am4charts.XYChart,
    dataByGroup: {[key: string]: Array<unknown>},
    measureName: string,
    dimension: Dimension
  ) {
    Object.entries(dataByGroup).forEach(([key, entries]) => {
      const name = removeDimensionSortPrefix(key)
      createSeries(name, entries)
      console.log("Created series", key, measureName, entries)
    })

    function createSeries(name: string, entries: Array<unknown>) {
      const series = chart.series.push(new am4charts.ColumnSeries())
      series.name = name
      series.data = entries
      series.dataFields.valueX = measureName
      series.dataFields.categoryY = "category"

      series.columns.template.fillOpacity = 0.35

      function colorAdapter(fill: Color, target: {name: string}) {
        const {color} = dimensionSettings(dimension, target.name)
        return color === undefined ? fill : am4core.color(color)
      }

      series.adapter.add("fill", colorAdapter)
      series.adapter.add("stroke", colorAdapter)
      series.clustered = true
      series.filters.push(dropShadowFilter())

      const labelBullet = series.bullets.push(new am4charts.LabelBullet())
      labelBullet.locationX = 0.5
      labelBullet.label.fill = am4core.color("#fff")

      return series
    }
  }
}

function toCategory(dataItem: any, dimensions: Array<string>) {
  return dimensions.map(dimension => removeDimensionSortPrefix(dataItem[dimension])).join("\n")
}

function updateChangeRateValues(
  selectedStartData: Array<any>,
  selectedEndData: Array<any>,
  measure: Measure,
  dimensions: Array<Dimension>,
  isAddressStateDimension: boolean
) {
  console.log("selectedStartData", selectedStartData)
  console.log("selectedEndData", selectedEndData)

  const selectedStartDataByKey = selectedStartData.reduce((acc, dataItem) => {
    const key = toKey(dataItem, dimensions)
    const value = dataItem[measure]
    return {...acc, [key]: value}
  }, {})
  console.log("selectedStartDataByKey", selectedStartDataByKey)

  if (isAddressStateDimension) {
    selectedEndData.forEach((dataItem: {[key: string]: number}) => {
      const key =
        dimensions.length > 1
          ? `${dataItem[dimensions[0]]}::${dataItem[dimensions[1]]}`
          : `${dataItem[dimensions[0]]}`
      dataItem.changeRate = calculateChangeRatePercent(
        dataItem[measure],
        _.get(selectedStartDataByKey, key)
      )
    })
  } else {
    selectedEndData.forEach((dataItem: {[k in Measure]: number} & {changeRate: number}) => {
      const key = toKey(dataItem, dimensions)
      const startValue = selectedStartDataByKey[key]

      // FIXME when series doesn't exist on start period
      dataItem.changeRate = calculateChangeRatePercent(dataItem[measure], startValue)

      console.log("Setting change rate to", key, startValue, dataItem)
    })
  }
}

function findDataWithEndDate(allData: Array<any>, endDate: Date): QueryData {
  const endDateFormatted = toCubeFormat(endDate)
  console.log("endDateFormatted", endDateFormatted)

  return _.chain(allData)
    .dropRightWhile(dataItem => dataItem[MemberSnapshotCube._atMonth] !== endDateFormatted)
    .takeRightWhile(dataItem => dataItem[MemberSnapshotCube._atMonth] === endDateFormatted)
    .value()
}

function findDataWithStartDate(allData: Array<any>, startDate: Date): QueryData {
  const startDateFormatted = toCubeFormat(startDate)
  console.log("startDateFormatted", startDateFormatted)

  return _.chain(allData)
    .dropWhile(dataItem => dataItem[MemberSnapshotCube._atMonth] !== startDateFormatted)
    .takeWhile(dataItem => dataItem[MemberSnapshotCube._atMonth] === startDateFormatted)
    .value()
}

function newChangeRateBullet(dimension: Dimension) {
  const changeBullet = new am4charts.LabelBullet()
  changeBullet.label.horizontalCenter = "left"
  changeBullet.label.dx = 10

  changeBullet.label.adapter.add("html", (value, label) => {
    if (label.dataItem === undefined) return undefined
    const changeRate = (<{changeRate: number}>label.dataItem.dataContext).changeRate
    return _.isFinite(changeRate)
      ? createChangeRateLabel(
          dimension,
          removeDimensionSortPrefix(
            (<{[k in Dimension]: string}>label.dataItem.dataContext)[dimension]
          ),
          {
            value: changeRate,
            unit: "%",
          }
        )
      : ""
  })
  changeBullet.label.truncate = false
  changeBullet.label.hideOversized = false
  changeBullet.label.fill = am4core.color("#000")

  return changeBullet
}

function groupDataByState(data: QueryData): Record<string, QueryData> {
  return _.groupBy(data, MemberCube.addressState)
}

function createMapImageSeriesData(
  dataByState: Record<string, QueryData | undefined>
): Array<{
  pieData: Array<{[key in (Dimension & string) | (Metric & string)]?: any}>
  latitude: number
  id: string
  radius: Percent
  longitude: number
}> {
  const pieTemplate = {
    NT: {
      radius: am4core.percent(14),
      latitude: -20.181884,
      longitude: 133.5293,
    },
    WA: {
      radius: am4core.percent(14),
      latitude: -25.486558,
      longitude: 123.001761,
    },
    NSW: {
      radius: am4core.percent(14),
      latitude: -30,
      longitude: 148,
    },
    VIC: {
      radius: am4core.percent(9),
      latitude: -37,
      longitude: 143,
    },
    TAS: {
      radius: am4core.percent(9),
      latitude: -43,
      longitude: 148.5,
    },
    QLD: {
      radius: am4core.percent(14),
      latitude: -20.917574,
      longitude: 145,
    },
    SA: {
      radius: am4core.percent(14),
      latitude: -30.000233,
      longitude: 135,
    },
    ACT: {
      radius: am4core.percent(9),
      latitude: -35.473469,
      longitude: 152.012375,
    },
  }

  return <any>Object.entries(dataByState).reduce<any>((acc, [state, data]) => {
    const template = (<any>pieTemplate)[state]
    // There are 'Unknown' and international ones
    if (template != null) {
      acc.push({
        ...(<any>pieTemplate)[state],
        ...{id: state, pieData: data},
      })
    }

    return acc
  }, [])
}
